<script setup lang="ts">
/*
 * The console tile — the one product unit on home channels, browse, search,
 * wishlist. Landscape art (16:9, `portrait` prop for 3:4) with the title and
 * price laid over a bottom scrim; the delivery chip top-left; heart and
 * add-to-cart appear on focus. Focusing or hovering a tile feeds its art to
 * the ambient backdrop. `data-tile` makes it a stop for the focus cursor.
 */
import { Heart, ShoppingCart } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useAddToCart } from '../composables/useAddToCart';
import { useFavoriteToggle } from '../composables/useFavoriteToggle';
import { PLATFORM_ICON_PATHS, platformIconFor } from '../data/platformIcons';
import { useUiStore } from '../stores/ui';
import { isPurchasable, productArt, productCover, type ProductSummary } from '../types/product';
import PriceTag from './PriceTag.vue';

const props = withDefaults(
  defineProps<{
    product: ProductSummary;
    portrait?: boolean;
    /** Rank ribbon for "top" channels. */
    rank?: number;
  }>(),
  { portrait: false, rank: undefined },
);

const ui = useUiStore();
const addToCart = useAddToCart();
const favorite = useFavoriteToggle();

const art = computed(() => (props.portrait ? productCover(props.product) : productArt(props.product)));
const purchasable = computed(() => isPurchasable(props.product));
const platformIcon = computed(() => platformIconFor(props.product.platform));
const isFavorite = computed(() => favorite.isFavorite(props.product.id));

const addButton = ref<HTMLElement | null>(null);
const justAdded = ref(false);
const ADDED_FLASH_MS = 1600;

function onFocus() {
  ui.setAmbient(productArt(props.product) ?? productCover(props.product));
}

function add() {
  if (!addToCart.add(props.product, { from: addButton.value })) return;
  justAdded.value = true;
  window.setTimeout(() => (justAdded.value = false), ADDED_FLASH_MS);
}
</script>

<template>
  <RouterLink
    :to="`/catalog/${product.id}`"
    data-tile
    class="tile group block"
    :class="portrait ? 'aspect-[3/4]' : 'aspect-video'"
    @focus="onFocus"
    @pointerenter="onFocus"
  >
    <img
      v-if="art"
      :src="art"
      :alt="product.name"
      loading="lazy"
      class="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 group-focus-visible:scale-105"
    />
    <div v-else class="absolute inset-0 flex items-center justify-center bg-bg-elev-2 text-text-faint">
      <svg v-if="platformIcon" class="h-10 w-10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path :d="PLATFORM_ICON_PATHS[platformIcon]" /></svg>
    </div>
    <div class="tile-scrim absolute inset-0" />

    <!-- Top row: delivery chip, platform mark, rank. -->
    <div class="absolute inset-x-3 top-3 flex items-start justify-between gap-2">
      <span v-if="rank" class="hud-number text-[28px] text-text-base drop-shadow-[0_2px_8px_rgba(0,0,0,.8)]">#{{ rank }}</span>
      <span v-else-if="product.isPreorder" class="chip-hot">{{ $t('product.card.delivery.preorder') }}</span>
      <span v-else-if="purchasable" class="chip-mint"><span class="animate-pulse-ring h-1.5 w-1.5 rounded-full bg-text-inv" />{{ $t('product.card.delivery.instant') }}</span>
      <span v-else class="chip">{{ $t('product.card.delivery.outOfStock') }}</span>
      <svg v-if="platformIcon" class="h-5 w-5 text-text-base/90 drop-shadow" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path :d="PLATFORM_ICON_PATHS[platformIcon]" /></svg>
    </div>

    <!-- Bottom: title + price; actions slide in on focus. -->
    <div class="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-3.5">
      <div class="min-w-0">
        <p class="line-clamp-2 font-heading text-[13px] font-bold leading-snug text-text-base drop-shadow">{{ product.name }}</p>
        <PriceTag :cents="product.ourPriceCents" size="sm" :from="(product.offerCount ?? 0) > 1" class="mt-1" />
      </div>
      <div class="flex shrink-0 translate-y-2 gap-1.5 opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100">
        <button
          type="button"
          class="group/heart btn-icon !h-9 !w-9"
          :class="isFavorite ? 'text-heart' : ''"
          :aria-label="$t('product.card.wishlist')"
          :aria-pressed="isFavorite"
          @click.prevent.stop="favorite.toggle(product)"
        >
          <Heart class="heartbeat-on-hover h-4 w-4" :fill="isFavorite ? 'currentColor' : 'none'" aria-hidden="true" />
        </button>
        <button
          v-if="purchasable"
          ref="addButton"
          type="button"
          class="btn-icon !h-9 !w-9"
          :class="justAdded ? '!bg-mint !text-text-inv' : '!bg-accent !text-text-inv'"
          :aria-label="$t('common.actions.addToCart')"
          @click.prevent.stop="add"
        >
          <ShoppingCart class="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  </RouterLink>
</template>
